import { useIntl } from "react-intl";
import { useMemo } from "react";
import { AgentMarkdown } from "@/components/chat/AgentMarkdown";
import { DocumentView } from "@/components/documents/DocumentView";
import { Disclosure } from "@/components/design/Disclosure";
import { useDocumentResourceFactory } from "@/lib/use-open-document";
import type { StudioDocument } from "./notebook-markdown";

/**
 * A document brought into the notebook: its name as a file card, its text (as the documents extraction read
 * it) one click away and read-only (Marvin, 2026-09-29). The text is context for the assistant, the card only
 * shows it; the file itself stays in the user's documents and opens from here.
 */
export function StudioDocumentCard({ document }: { document: StudioDocument }) {
  const intl = useIntl();
  const createResource = useDocumentResourceFactory();
  const resource = useMemo(() => createResource(document.documentId, document.name), [createResource, document.documentId, document.name]);
  return (
    <Disclosure id={`studio-document-${document.documentId}-text`}
      title={
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{document.name}</span>
        </span>
      }
      headerAction={
        <DocumentView file={resource} openLabel={intl.formatMessage({ id: "studio.document.open", defaultMessage: "Open the document" })} />
      }
    >
      <div className="max-h-96 overflow-auto px-5 py-3 text-smycles-body leading-relaxed text-ink">
        <AgentMarkdown content={document.text} />
      </div>
    </Disclosure>
  );
}
